import React, { Fragment, useMemo, useState } from "react";
import { AppBar } from "../ui/app-bar";
import { Redirect, useLocalSearchParams } from "expo-router";
import { ScrollView, View, Text } from "react-native";
import { Avatar } from "../ui/avatar";
import { useFormatDate } from "@/elements/hooks/use-format-date";
import ClockIcon from "@/assets/svgs/clock-icon";
import SubjectIcon from "@/assets/svgs/subject-icon";
import RemoteIcon from "@/assets/svgs/remote-icon";
import { Tab } from "../ui/tab";
import Markdown from "react-native-markdown-display";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "../utils/convex-api";
import { Id } from "@teachloop/backend/_generated/dataModel";
import Loader from "../shared/loader";
import UserIcon from "@/assets/svgs/user-icon";
import ResponsiveContent from "../shared/responsive-content";
import StudyMaterialList from "./study-material-list";
import LessonReportWellbeingTab from "./lesson-report-wellbeing-tab";
import goBack from "../utils/go-back";

const markdownStyles = {
  body: { fontFamily: "Geologica_400Regular", color: "#181718" },
  heading1: { fontFamily: "Geologica_500Medium", color: "#181718" },
  heading2: { fontFamily: "Geologica_500Medium", color: "#181718" },
  heading3: { fontFamily: "Geologica_500Medium", color: "#181718" },
  heading4: { fontFamily: "Geologica_500Medium", color: "#181718" },
  heading5: { fontFamily: "Geologica_500Medium", color: "#181718" },
  heading6: { fontFamily: "Geologica_500Medium", color: "#181718" },
  text: { color: "#181718" },
  strong: { fontFamily: "Geologica_500Medium", color: "#181718" },
};

function LessonReportTab({
  initialData,
}: {
  initialData: {
    topicsCovered: string | null | undefined;
    strengths: string | null | undefined;
    challenges: string | null | undefined;
    studentComments: string | null | undefined;
    tutorComments: string | null | undefined;
  };
}) {
  const topicsCovered = initialData.topicsCovered ?? "";
  const strengths = initialData.strengths ?? "";
  const challenges = initialData.challenges ?? "";
  const studentComments = initialData.studentComments ?? "";
  const tutorComments = initialData.tutorComments ?? "";

  return (
    <View className="gap-6">
      <View className="gap-2">
        <View className="flex-row items-center justify-between">
          <Text className="text-base font-geologica-medium font-medium text-gray-800">
            Topics covered
          </Text>
        </View>

        <View className="py-3">
          <Markdown style={markdownStyles}>{topicsCovered}</Markdown>
        </View>
      </View>

      <View className="gap-2">
        <View className="flex-row items-center justify-between">
          <Text className="text-base font-geologica-medium font-medium text-gray-800">
            Strengths
          </Text>
        </View>

        <View className="py-3">
          <Markdown style={markdownStyles}>{strengths}</Markdown>
        </View>
      </View>

      <View className="gap-2">
        <View className="flex-row items-center justify-between">
          <Text className="text-base font-geologica-medium font-medium text-gray-800">
            Challenges
          </Text>
        </View>

        <View className="py-3">
          <Markdown style={markdownStyles}>{challenges}</Markdown>
        </View>
      </View>

      <View className="gap-2">
        <View className="flex-row items-center justify-between">
          <Text className="text-base font-geologica-medium font-medium text-gray-800">
            Student comments
          </Text>
        </View>

        <View className="py-3">
          <Markdown style={markdownStyles}>{studentComments}</Markdown>
        </View>
      </View>

      <View className="gap-2">
        <View className="flex-row items-center justify-between">
          <Text className="text-base font-geologica-medium font-medium text-gray-800">
            Tutor comments
          </Text>
        </View>

        <View className="py-3">
          <Markdown style={markdownStyles}>{tutorComments}</Markdown>
        </View>
      </View>
    </View>
  );
}

function LessonReportNextStepsTab({
  initialData,
}: {
  initialData: { nextSteps: string | null | undefined };
}) {
  const nextSteps = initialData.nextSteps ?? "";

  return (
    <View className="gap-6">
      <View className="gap-2">
        <View className="flex-row items-center justify-between">
          <Text className="text-base font-geologica-medium font-medium text-gray-800">
            Next steps
          </Text>
        </View>

        <View className="py-3">
          <Markdown style={markdownStyles}>{nextSteps}</Markdown>
        </View>
      </View>
    </View>
  );
}

function LessonReport() {
  const fmt = useFormatDate();
  const { lessonReportId, tab } = useLocalSearchParams<{
    lessonReportId?: Id<"lessonReports">;
    tab?: string;
  }>();
  const initialTab = useMemo(() => {
    if (tab === "well-being") return "well-being" as const;
    if (tab === "next-steps") return "next-steps" as const;
    return "report" as const;
  }, [tab]);
  const [activeTab, setActiveTab] = useState<
    "report" | "next-steps" | "well-being"
  >(initialTab);
  const { data: lessonReport, isLoading } = useQuery(
    convexQuery(
      api.student.lessonReport.queries.getLessonReport,
      lessonReportId
        ? {
            lessonReportId: lessonReportId,
          }
        : "skip"
    )
  );

  if (isLoading) {
    return <Loader />;
  }

  if (!lessonReportId) {
    return <Redirect href="/" />;
  }

  return (
    <Fragment>
      <AppBar>
        <AppBar.Icon onPress={goBack} />
        <AppBar.Title>Back</AppBar.Title>
      </AppBar>
      <ScrollView>
        <ResponsiveContent>
          <View className="mx-5">
            <View className="h-2" />
            <View className="gap-8">
              <View className="gap-1">
                <Text className="text-[32px] text-gray-800 leading-[1.2] font-medium font-zilla-slab-medium">
                  Lesson report
                </Text>
                <Text className="text-xl font-geologica-regular font-normal leading-[1.5] text-gray-800">
                  {lessonReport?.completedAt
                    ? fmt(lessonReport?.completedAt, "EEE, do MMMM")
                    : ""}
                </Text>
              </View>

              <View className="h-[1px] bg-gray-200" />

              <View className="flex-row justify-between items-center">
                <View className="flex-row gap-2">
                  <Text className="text-base font-geologica-medium font-medium text-gray-800">
                    Student
                  </Text>
                  <Avatar size="sm">
                    <Avatar.Content
                      labelSize="sm"
                      size="sm"
                      label={lessonReport?.student?.name}
                      source={
                        lessonReport?.student?.imageUrl
                          ? { uri: lessonReport?.student?.imageUrl }
                          : undefined
                      }
                    />
                  </Avatar>
                  <Text className="text-base font-geologica-regular font-normal text-gray-800">
                    {lessonReport?.student?.name}
                  </Text>
                </View>
                <View className="flex-row gap-2">
                  <Text className="text-base font-geologica-medium font-medium text-gray-800">
                    Tutor
                  </Text>
                  <Avatar size="sm">
                    <Avatar.Content
                      labelSize="sm"
                      size="sm"
                      label={lessonReport?.tutor?.name}
                      source={
                        lessonReport?.tutor?.imageUrl
                          ? { uri: lessonReport?.tutor?.imageUrl }
                          : undefined
                      }
                    />
                  </Avatar>
                  <Text className="text-base font-geologica-regular font-normal text-gray-800">
                    {lessonReport?.tutor?.name}
                  </Text>
                </View>
              </View>

              <View className="flex-row">
                <View className="flex-1">
                  <View className="flex-row items-center gap-2">
                    <SubjectIcon
                      className="size-6"
                      pathClassName={"stroke-gray-800"}
                    />
                    <Text
                      className={`font-geologica-medium font-medium text-gray-800 text-base leading-[1.5]`}
                    >
                      Subject
                    </Text>
                  </View>
                  <Text
                    className={`font-geologica-regular font-normal text-gray-800 text-base leading-[1.5]`}
                  >
                    {lessonReport?.session?.subject}
                  </Text>
                </View>
                <View className="flex-1">
                  <View className="flex-row items-center gap-2">
                    {lessonReport?.session?.deliveryMethod === "in_person" ? (
                      <UserIcon
                        className="size-6"
                        pathClassName={"stroke-gray-800"}
                      />
                    ) : (
                      <RemoteIcon
                        className="size-6"
                        pathClassName={"stroke-gray-800"}
                      />
                    )}
                    <Text
                      className={`font-geologica-medium font-medium text-gray-800 text-base leading-[1.5]`}
                    >
                      Session
                    </Text>
                  </View>
                  <Text
                    className={`font-geologica-regular font-normal text-gray-800 text-base leading-[1.5]`}
                  >
                    {lessonReport?.session?.deliveryMethod === "in_person"
                      ? "In person"
                      : "Remote"}
                  </Text>
                </View>
                <View className="flex-1">
                  <View className="flex-row items-center gap-2">
                    <ClockIcon
                      className="size-6"
                      pathClassName={"stroke-gray-800"}
                    />
                    <Text
                      className={`font-geologica-medium font-medium text-gray-800 text-base leading-[1.5]`}
                    >
                      Time
                    </Text>
                  </View>
                  {!!lessonReport?.session?.startTime &&
                    !!lessonReport?.session?.endTime && (
                      <Text
                        className={`font-geologica-regular font-normal text-gray-800 text-base leading-[1.5]`}
                      >
                        {fmt(lessonReport?.session?.startTime, "HH:mm")} -{" "}
                        {fmt(lessonReport?.session?.endTime, "HH:mm")}
                      </Text>
                    )}
                </View>
              </View>

              <View className="h-[1px] bg-gray-200" />

              <StudyMaterialList files={lessonReport?.files ?? []} />

              <View className="h-[1px] bg-gray-200" />

              <View className="flex-row gap-2">
                <Tab
                  size="md"
                  intent={activeTab === "report" ? "active" : "inActive"}
                  onPress={() => setActiveTab("report")}
                >
                  <Tab.Text>Report</Tab.Text>
                </Tab>
                <Tab
                  size="md"
                  intent={activeTab === "next-steps" ? "active" : "inActive"}
                  onPress={() => setActiveTab("next-steps")}
                >
                  <Tab.Text>Next steps</Tab.Text>
                </Tab>
                <Tab
                  size="md"
                  intent={activeTab === "well-being" ? "active" : "inActive"}
                  onPress={() => setActiveTab("well-being")}
                >
                  <Tab.Text>Well-being</Tab.Text>
                </Tab>
              </View>

              {activeTab === "report" && (
                <LessonReportTab
                  initialData={{
                    topicsCovered: lessonReport?.topics,
                    strengths: lessonReport?.strengths,
                    challenges: lessonReport?.challenges,
                    studentComments: lessonReport?.studentComment,
                    tutorComments: lessonReport?.tutorComment,
                  }}
                />
              )}
              {activeTab === "next-steps" && (
                <LessonReportNextStepsTab
                  initialData={{
                    nextSteps: lessonReport?.nextSteps,
                  }}
                />
              )}
              {activeTab === "well-being" && (
                <LessonReportWellbeingTab
                  initialData={{
                    aiWellbeingReport: null,
                    studentMood: lessonReport?.studentMood,
                    studentMoodComment: lessonReport?.studentMoodComment,
                    studentComment: lessonReport?.studentComment,
                    studentName: lessonReport?.student?.name,
                  }}
                />
              )}
            </View>
            <View className="h-8" />
          </View>
        </ResponsiveContent>
      </ScrollView>
    </Fragment>
  );
}

export default LessonReport;
